import {
  Component,
  inject,
  OnDestroy,
  signal,
  computed,
  ChangeDetectionStrategy,
  input,
  output,
  isDevMode,
} from '@angular/core';
import {
  AlertService,
  ButtonLoadingDirective,
  CardComponent,
  CardContentComponent,
  CardHeaderComponent,
  IconComponent,
  IconKey,
  LoadingComponent,
} from '@gains-com/ui-angular';
import { CurrencyPipe, formatDate, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse, httpResource } from '@angular/common/http';
import { MatButton } from '@angular/material/button';
import { RedemptionDialogHeaderComponent } from '../redemption-dialog-header/redemption-dialog-header.component.js';
import { AnalyticsEvent, decimal, generateId, PaymentMethodEntityTypeId } from '@gains-com/shared';
import { PaymentMethodsService } from '../../../../core/payment-methods.js';
import {
  CoinflowFeeEstimate,
  CoinflowWithdrawalSpeed,
  RedemptionsService,
} from '../../../../core/redemption.service.js';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { CoinflowService } from '../../../../core/coinflow.js';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { WindowProvider } from '../../../../core/window.provider.js';
import { environment } from '../../../../../environments/environment.js';
import { CoinflowCardNewPaymentMethod, CoinflowRedemptionPaymentMethod } from '../../common.js';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, exhaustMap, filter, finalize, from, map, of, Subject, switchMap, tap } from 'rxjs';
import { Analytics } from '../../../../core/analytics/analytics.service.js';
import { ActiveRedemptionViewService } from '../../../../core/view-states/active-redemption-view.service.js';
import { UserViewService } from '../../../../core/view-states/user-view.service.js';
import { PaymentMethodsViewService } from '../../../../core/view-states/payment-methods-view.service.js';
import { AccountLimitsViewService } from '../../../../core/view-states/account-limits-view.service.js';
import { LOGGER_FACTORY } from '../../../../core/logger/logger-factory.provider.js';

interface PaymentMethodVM {
  payment_method_id: string;
  name: string;
  icon: IconKey;
  type: 'card' | 'bank_account';
  same_day: boolean;
  fee: number;
  approved: boolean;
}

@Component({
  selector: 'app-coinflow-redemption',
  imports: [
    CardComponent,
    CardContentComponent,
    RedemptionDialogHeaderComponent,
    MatButton,
    ButtonLoadingDirective,
    CurrencyPipe,
    FormsModule,
    ReactiveFormsModule,
    LoadingComponent,
    IconComponent,
    CardHeaderComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './coinflow-redemption.component.html',
  styleUrls: ['../styles.scss', './coinflow-redemption.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoinflowRedemptionComponent implements OnDestroy {
  // Service Injections
  private readonly _redemptionsApi = inject(RedemptionsService);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _activeRedemptionViewService = inject(ActiveRedemptionViewService);
  private readonly _coinflowService = inject(CoinflowService);
  private readonly _window = inject(WindowProvider);
  private readonly _analytics = inject(Analytics);
  private readonly _userView = inject(UserViewService);
  private readonly _paymentMethodsViewService = inject(PaymentMethodsViewService);
  private readonly _paymentMethodsService = inject(PaymentMethodsService);
  private readonly _accountLimitsView = inject(AccountLimitsViewService);
  private readonly _alertService = inject(AlertService);

  private readonly _logger = inject(LOGGER_FACTORY).create('CoinflowRedemption');

  // Input/Output Properties
  amount = input.required<number>();
  back = output<void>();
  close = output<void>();

  // Form Controls
  paymentMethodId = new FormControl<string | null>(null);

  // Observable to Signal Conversions
  private readonly _allPaymentMethods = toSignal(this._paymentMethodsViewService.visiblePaymentMethods$);
  userState = toSignal(this._userView.state$);
  dailyRedemptionLimit = toSignal(this._accountLimitsView.dailyRedemptionLimits$);
  private _paymentMethodId = toSignal(this.paymentMethodId.valueChanges);

  // Computed Signals
  nextAvailableDate = computed(() => formatDate(new Date(new Date().setUTCHours(24, 0, 0, 0)), 'short', 'en-US'));
  totalRedemptionAmount = computed(() => decimal(this.amount()).minus(this.paymentMethod()!.fee).toNumber());
  paymentMethods = computed(
    () =>
      (this._allPaymentMethods()?.filter((p) =>
        (
          p.visible_to_user &&
          ([
            PaymentMethodEntityTypeId.CoinflowBankAccount,
            PaymentMethodEntityTypeId.CoinflowDebitCard,
          ] as PaymentMethodEntityTypeId[])
        ).includes(p.payment_method_entity_type_id),
      ) as CoinflowRedemptionPaymentMethod[] | undefined) ?? null,
  );
  paymentMethodsVM = computed(() => this.paymentMethods()?.map((p) => this.paymentMethodToVM(p)) ?? []);
  cards = computed(() => this.paymentMethodsVM().filter((p) => p.type === 'card'));
  bankAccounts = computed(() => this.paymentMethodsVM().filter((p) => p.type === 'bank_account'));
  hasPlaythroughRequirement = computed(
    () =>
      !!this.userState()?.playthrough_requirement_id &&
      decimal(this.userState()?.playthrough_progress ?? 0).lt(decimal(this.userState()?.playthrough_requirement ?? 0)),
  );
  paymentMethod = computed(() => this.paymentMethodsVM().find((p) => p.payment_method_id === this._paymentMethodId()));
  dailyLimitExceeded = computed(() => {
    const dailyLimit = this.dailyRedemptionLimit();

    if (!dailyLimit) {
      return false;
    }

    const existingAmount = decimal(dailyLimit.daily_number);
    const limit = decimal(dailyLimit?.daily_redemptions_number_limit ?? 0);

    return existingAmount.add(1).gt(limit);
  });
  loading = computed(() => this.pmSyncState() === 'syncing' || !this.paymentMethods()?.length || this.submitting());
  fees = computed(
    () =>
      Object.fromEntries(
        (this._fees() ?? []).map((f) => [f.speed, Number(f.feeAmountInTransactionCurrency)]),
      ) as Record<CoinflowWithdrawalSpeed, number>,
  );
  // The fee estimate fetch fires and the picker shows its spinner under the same
  // condition: the method list exists but the estimate hasn't loaded yet. Share
  // one predicate so the trigger (constructor) and the spinner never drift.
  private readonly _feesPending = computed(() => !!this.paymentMethods()?.length && !this._fees());
  feesLoading = this._feesPending;

  // Reactive Signals
  // Tracks the synchronous Coinflow redemption-method reconcile that runs on
  // init. 'syncing' blocks the picker behind a spinner; 'stale' unblocks with
  // the cached list (fail-open, payout-time revalidation is the backstop).
  pmSyncState = signal<'syncing' | 'fresh' | 'stale'>('syncing');
  submitting = signal(false);
  redemptionCreateInProgress = signal(false);
  addPaymentMethodLoading = signal(false);
  iframeLoaded = signal(false);
  formComplete = signal(false);
  coinflowIframeUrl = signal<SafeUrl | null>(null);
  _fees = signal<CoinflowFeeEstimate[] | null>(null);

  _iframeTimeout: any;

  // Fires the synchronous Coinflow redemption-method reconcile — once on init and
  // again each time the add-payment-method iframe closes (see `_onIframeDone`).
  private readonly _reconcilePaymentMethods$ = new Subject<void>();

  // Constants
  idempotencyKey = generateId();

  paymentMethodToVM = (p: CoinflowRedemptionPaymentMethod): PaymentMethodVM => {
    if (p.payment_method_entity_type_id === PaymentMethodEntityTypeId.CoinflowBankAccount) {
      return {
        approved: p.approved_by_payment_provider,
        payment_method_id: p.payment_method_id,
        type: 'bank_account',
        icon: 'payment:bank_account',
        name: p.label,
        fee: Math.min(this.fees()?.['standard'] ?? 1, this.fees()?.['same_day'] ?? 1),
        same_day:
          this.fees()?.['same_day'] &&
          this.fees()?.['standard'] &&
          this.fees()!['same_day'] <= this.fees()!['standard'],
      } as PaymentMethodVM;
    } else {
      const card = p as CoinflowCardNewPaymentMethod;
      let cardIcon: IconKey;

      switch (card.metadata!.type) {
        case 'VISA':
          cardIcon = 'payment:visa';
          break;
        case 'MSTR':
          cardIcon = 'payment:mastercard';
          break;
        case 'AMEX':
          cardIcon = 'payment:amex';
          break;
        case 'DISC':
          cardIcon = 'payment:discover';
          break;
        default:
          cardIcon = 'payment:debit_credit_card';
      }

      return {
        payment_method_id: p.payment_method_id,
        type: 'card',
        icon: cardIcon,
        name: p.label,
        fee: this.fees()?.['card'] ?? 2,
        same_day: false,
        approved: p.approved_by_payment_provider,
      };
    }
  };

  handleCoinflowError(err: any, source: 'iframe' | 'fee' | 'iframe-load') {
    const blocked =
      err?.error?.code === 'PAYMENT_PROVIDER_BLOCKED'
        ? this._redemptionsApi.ErrorMap['PAYMENT_PROVIDER_BLOCKED']
        : undefined;

    this._alertService.notifyDanger(
      blocked?.title ?? 'An unexpected error occurred',
      blocked?.message ?? 'Please try again in a few minutes',
    );
    this._logger.error('Coinflow redemption error', {
      source,
      err,
    });

    this.back.emit();
  }

  private readonly _onIframeDone = (event: MessageEvent) => {
    if (event.data === 'coinflow-redirect') {
      this.coinflowIframeUrl.set(null);
      this.iframeLoaded.set(false);
      this._window.removeEventListener('message', this._onIframeDone);
      // Re-run the reconcile so the newly-added method is fetched into shared
      // state — the spinner honestly reflects that work, then the picker shows
      // the new method. Fire-and-forget (never re-hydrating) left the dialog
      // stuck on the spinner for users who started with zero methods.
      this.pmSyncState.set('syncing');
      this._reconcilePaymentMethods$.next();
    }
  };

  ngOnDestroy() {
    this._window.removeEventListener('message', this._onIframeDone);
  }

  handleIframeLoad() {
    this.iframeLoaded.set(true);
    this._iframeTimeout && clearTimeout(this._iframeTimeout);
  }

  async addPaymentMethod() {
    this.addPaymentMethodLoading.set(true);
    try {
      const coinflowKey = await this._coinflowService.createSessionToken('bank_linking');
      this.coinflowIframeUrl.set(
        this._sanitizer.bypassSecurityTrustResourceUrl(
          `https://${
            environment.coinflow.env === 'prod' ? '' : 'sandbox.'
          }coinflow.cash/solana/withdraw/${environment.coinflow.merchantId}?` +
            new URLSearchParams({
              sessionKey: coinflowKey,
              allowedWithdrawSpeeds: 'standard,same_day,card',
              bankAccountLinkRedirect: `data:text/html;base64,${btoa(
                '<html><body><script>window.top.postMessage("coinflow-redirect", "*");</script></body></html>',
              )}`,
            }).toString(),
        ),
      );

      this._window.addEventListener('message', this._onIframeDone);

      this._iframeTimeout && clearTimeout(this._iframeTimeout);
      this._iframeTimeout = setTimeout(() => {
        this.handleCoinflowError(new Error('Loading Coinflow iframe timeout'), 'iframe-load');
      }, 10e3);
    } catch (err) {
      this.handleCoinflowError(err, 'iframe');
    } finally {
      this.addPaymentMethodLoading.set(false);
    }
  }

  async completeForm() {
    this.paymentMethodId.markAllAsTouched();

    if (!this.paymentMethodId.valid) {
      return;
    }

    this.formComplete.set(true);
  }

  async submit() {
    this.submitting.set(true);
  }

  selectPaymentMethod(paymentMethodId: string | null) {
    this.paymentMethodId.setValue(paymentMethodId);

    if (paymentMethodId) {
      this.completeForm();
    } else {
      this.formComplete.set(false);
    }
  }

  // Trigger that opens once the (approved) payment method is ready and the
  // user has pressed submit. Emitting `null` keeps the gate closed.
  private readonly _submitTrigger = computed(() => {
    const paymentMethod = this.paymentMethod();

    return paymentMethod && paymentMethod.approved && this.submitting() ? paymentMethod : null;
  });

  constructor() {
    // Coinflow fee estimate. Fetched eagerly as soon as the payment-method list
    // is available — NOT on selection — so the picker shows the real per-speed
    // fees instead of the `?? 1`/`?? 2` fallback defaults. The estimate
    // is amount-based, so it doesn't need a selected method. Fetched once:
    // `_feesPending` goes false after the first successful `_fees` set (it's the
    // same predicate that drives the picker spinner). switchMap keeps the latest
    // in-flight request.
    toObservable(this._feesPending)
      .pipe(
        filter((pending) => pending),
        switchMap(() =>
          from(this._redemptionsApi.estimateCoinflowRedemptionFee(this.amount().toString())).pipe(
            catchError((err) => {
              this.handleCoinflowError(err, 'fee');
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe((fees) => this._fees.set(fees));

    // Reconcile the user's saved Coinflow redemption methods against Coinflow's
    // live withdrawer state before they can pick a (possibly deleted) token.
    // Runs on init and again whenever the add-method iframe closes (see
    // `_onIframeDone`). switchMap so the latest trigger always wins and resolves
    // pmSyncState — `_onIframeDone` flips state to 'syncing' before firing, and a
    // dropped trigger would otherwise strand the spinner.
    //
    // Whether or not the sync POST succeeds, ALWAYS refresh the full shared state
    // via syncPaymentMethods() (GET-all) — not setPaymentMethods(), which would
    // wipe crypto/other methods. Fail open: a sync error (or stale:true) still
    // surfaces whatever is already persisted, so the picker unblocks with the
    // cached list instead of hanging.
    this._reconcilePaymentMethods$
      .pipe(
        switchMap(() =>
          from(this._paymentMethodsService.syncCoinflowRedemptionPaymentMethods()).pipe(
            map((response) => response.stale),
            catchError((err) => {
              this._logger.error('Failed to sync Coinflow redemption payment methods', { err });
              return of(true);
            }),
            switchMap((stale) =>
              from(this._paymentMethodsViewService.syncPaymentMethods()).pipe(
                map((): 'stale' | 'fresh' => (stale ? 'stale' : 'fresh')),
                catchError((err) => {
                  this._logger.error('Failed to refresh payment methods after Coinflow sync', { err });
                  return of('stale' as const);
                }),
              ),
            ),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe((state) => this.pmSyncState.set(state));

    // Kick off the initial reconcile.
    this._reconcilePaymentMethods$.next();

    // Auto-open the add-payment-method iframe when the user has none — but only
    // once the sync has resolved, so it doesn't fire on the initial (pre-sync)
    // empty state before the reconcile populates the list.
    toObservable(computed(() => ({ state: this.pmSyncState(), methods: this.paymentMethods() })))
      .pipe(
        filter(({ state }) => state !== 'syncing'),
        takeUntilDestroyed(),
      )
      .subscribe(({ methods }) => {
        if (methods?.length === 0) {
          this.addPaymentMethod();
        }
      });

    // Create the redemption once submitted. exhaustMap ignores further
    // emissions while a create is in flight (replaces redemptionCreateInProgress).
    toObservable(this._submitTrigger)
      .pipe(
        filter((paymentMethod): paymentMethod is NonNullable<typeof paymentMethod> => !!paymentMethod),
        exhaustMap((paymentMethod) => {
          this.redemptionCreateInProgress.set(true);

          return from(
            this._redemptionsApi.createRedemption(
              this.idempotencyKey,
              paymentMethod.payment_method_id,
              this.amount().toString(),
            ),
          ).pipe(
            tap((redemption) => {
              this._analytics.trackEvent(AnalyticsEvent.RedemptionInitiated, {
                $insert_id: redemption.redemption_id,
                value: this.amount(),
                currency: 'USD',
                provider: 'coinflow',
                transaction_id: redemption.redemption_id,
              });

              this._activeRedemptionViewService.setActiveRedemption(redemption);
            }),
            catchError((err: any) => {
              const e = err as HttpErrorResponse;
              this.idempotencyKey = generateId();
              this._logger.error('Failed to create redemption', { error: err });

              if (isDevMode()) {
                console.error(err);
              }

              let errorMessage = e.error?.message ?? e.message;
              let errorTitle = 'Redemption Failed';

              if (e.error?.code) {
                const errHandle = this._redemptionsApi.ErrorMap?.[e?.error?.code];

                if (errHandle) {
                  errorMessage = errHandle.message;
                  errorTitle = errHandle.title;
                }
              }

              this._alertService.notifyDanger(errorTitle, errorMessage);
              return EMPTY;
            }),
            finalize(() => {
              this.submitting.set(false);
              this.redemptionCreateInProgress.set(false);
            }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }
}
